/**
 * Parser for the AI agent response stored on a service request.
 *
 * The backend sometimes double-encodes JSON, so we recursively unwrap
 * up to 3 layers before trying to extract sections.
 */

export interface AiSection {
  title: string;
  body:  string;
}

export interface ParsedAiResponse {
  sections: AiSection[];
  rawText:  string;
}

export function parseAiResponse(raw: string | null | undefined): ParsedAiResponse {
  const empty: ParsedAiResponse = { sections: [], rawText: '' };
  if (!raw || typeof raw !== 'string') return empty;

  let parsed: unknown = raw;
  for (let i = 0; i < 3; i++) {
    if (typeof parsed === 'string') {
      try { parsed = JSON.parse(parsed); } catch { break; }
    } else {
      break;
    }
  }

  // Known structure: { sections: [{ title, body }] }
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    const obj = parsed as Record<string, unknown>;
    if (Array.isArray(obj.sections)) {
      const sections = (obj.sections as unknown[])
        .filter((s): s is { title: unknown; body: unknown } => typeof s === 'object' && s !== null)
        .map(s => ({ title: String(s.title ?? ''), body: String(s.body ?? '') }))
        .filter(s => s.title || s.body);
      if (sections.length > 0) return { sections, rawText: raw };
    }
  }

  // Array wrapper: pick the first entry with ai_response / response_data
  if (Array.isArray(parsed)) {
    const entry = (parsed as unknown[]).find(
      (r): r is { ai_response?: string; response_data?: string } =>
        typeof r === 'object' && r !== null,
    );
    const inner =
      (entry as { ai_response?: string; response_data?: string } | undefined)?.ai_response ??
      (entry as { ai_response?: string; response_data?: string } | undefined)?.response_data ??
      '';
    if (inner) return parseAiResponse(inner);
  }

  const text = typeof parsed === 'string' ? parsed : raw;

  // Full Markdown document (e.g. advance_tax_planning): the backend returns a
  // string with Markdown headings + lists, not the { sections } JSON shape.
  // Render it whole through MarkdownRenderer as one untitled section — do NOT
  // run the numbered-heading splitter below, which would discard everything
  // before the first numbered line (the intro + the entire financial summary).
  if (typeof parsed === 'string' && /^#{1,6}\s/m.test(text)) {
    return { sections: [{ title: '', body: text }], rawText: raw };
  }

  // Fallback: split unstructured text (no Markdown headings) on numbered headings
  const numberPattern = /(?:^|\n)(\d+)\.\s+([^\n]+)/g;
  const sections: AiSection[] = [];
  let last = 0;
  let match: RegExpExecArray | null;

  // eslint-disable-next-line no-cond-assign
  while ((match = numberPattern.exec(text)) !== null) {
    const body = text.slice(last, match.index).trim();
    if (body && sections.length > 0) sections[sections.length - 1]!.body += '\n\n' + body;
    sections.push({ title: match[2]!.trim(), body: '' });
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    const tail = text.slice(last).trim();
    if (sections.length > 0) {
      sections[sections.length - 1]!.body +=
        (sections[sections.length - 1]!.body ? '\n\n' : '') + tail;
    } else {
      sections.push({ title: 'Analysis', body: tail });
    }
  }

  return sections.length > 0
    ? { sections, rawText: raw }
    : { sections: [{ title: 'AI Analysis', body: text }], rawText: raw };
}
